import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  ogImage,
  twitterCard = 'summary_large_image',
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setOrCreateMeta = (attr: string, value: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${attr}"]` : `meta[name="${attr}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) {
          el.setAttribute('property', attr);
        } else {
          el.setAttribute('name', attr);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    const removeMetaIfExists = (attr: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${attr}"]` : `meta[name="${attr}"]`;
      const el = document.querySelector(selector);
      if (el) el.remove();
    };

    setOrCreateMeta('description', description);
    if (keywords) {
      setOrCreateMeta('keywords', keywords);
    } else {
      removeMetaIfExists('keywords');
    }

    if (ogTitle) setOrCreateMeta('og:title', ogTitle, true);
    if (ogDescription) setOrCreateMeta('og:description', ogDescription, true);
    setOrCreateMeta('og:type', ogType, true);
    if (ogUrl) setOrCreateMeta('og:url', ogUrl, true);
    if (ogImage) setOrCreateMeta('og:image', ogImage, true);

    setOrCreateMeta('twitter:card', twitterCard);
    if (ogTitle) setOrCreateMeta('twitter:title', ogTitle);
    if (ogDescription) setOrCreateMeta('twitter:description', ogDescription);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    if (jsonLd) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach((s) => s.remove());

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogUrl, ogImage, twitterCard, jsonLd]);

  return null;
}